import React, { ReactElement, useEffect, useState } from 'react';
import CmonHaProxyStat from '../../../types/CmonHaProxyStat';
import FriendlyTimestamp from '../../../types/FriendlyTimestamp';
import AppChart from '../../AppChart';
import prettyBytes from 'pretty-bytes';
import CcxComponentProps from '../../../core/CcxComponent';
import ChartCustomTooltip from './ChartCustomTooltip';

interface Props extends CcxComponentProps {
    data?: CmonHaProxyStat[];
    loading: boolean;
    displaySummary?: boolean;
    height?: number;
    width?: number;
}

function HaProxyBackendTrafficChart({
    data,
    loading,
    displaySummary,
    testId = 'HaProxyBackendTrafficChart',
    width,
    height = 250,
}: Props): ReactElement {
    const [chartData, setChartData] = useState<any>([]);
    const [summary, setSummary] = useState<any>([]);

    const setChartSummary = (data: any, series: any) => {
        let max: number[] = [];
        let min: number[] = [];
        let sum: number[] = [];
        let avg: number[] = [];

        series.forEach((s: any) => {
            max[s.value] = 0;
            min[s.value] = 0;
            sum[s.value] = 0;
            avg[s.value] = 0;
        });

        data.forEach((d: any) => {
            series.forEach((s: any) => {
                sum[s.value] += d[s.value];

                min[s.value] =
                    d[s.value] < min[s.value] ? d[s.value] : min[s.value];
                max[s.value] =
                    d[s.value] > max[s.value] ? d[s.value] : max[s.value];
            });
        });

        series.forEach((s: any) => {
            avg[s.value] = sum[s.value] / data.length;
        });

        const dataSummary = series.map((s: any) => {
            return {
                serie: s.value,
                label: s.label,
                avg: prettyBytes(avg[s.value]),
                min: prettyBytes(min[s.value]),
                max: prettyBytes(max[s.value]),
                sum: prettyBytes(sum[s.value]),
            };
        });

        setSummary(dataSummary);
    };

    useEffect(() => {
        if (data && data.length > 0) {
            const formattedData = data
                .filter((stat: CmonHaProxyStat) => {
                    return stat.isBackend();
                })
                .map((stat: CmonHaProxyStat) => {
                    return [
                        {
                            yValue: stat.bIn,
                            series: `Bytes In`,
                            xValue: new FriendlyTimestamp(
                                stat.created
                            ).getDate(),
                        },
                        {
                            yValue: stat.bOut,
                            series: `Bytes Out`,
                            xValue: new FriendlyTimestamp(
                                stat.created
                            ).getDate(),
                        },
                    ];
                });
            setChartData(formattedData.flat());

            const summarySeries = [
                {
                    value: 'bIn',
                    label: 'Bytes In',
                },
                {
                    value: 'bOut',
                    label: 'Bytes Out',
                },
            ];
            setChartSummary(data, summarySeries);
        }
    }, [data]);

    const chartConfig = {
        title: {
            visible: true,
            text: 'Backend Traffic',
        },
        description: {
            visible: true,
            text: '',
        },
        padding: 'auto',
        height: height,
        width: width,
        autoFit: !width && !height,
        xField: 'xValue',
        yField: 'yValue',
        xAxis: {
            type: 'time',
            mask: 'hh:mm',
        },
        yAxis: {
            label: {
                formatter: (text: string, item: any, index: number) => {
                    return prettyBytes(parseInt(text, 10));
                },
            },
        },
        animation: false,
        seriesField: 'series',
        tooltip: {
            customContent: (title: any, data: any) => {
                const getTitle = () => {
                    if (!data || data.length === 0) return '';
                    return data[0]?.data?.xValue.toString();
                };

                const getContent = () => {
                    if (!data || data.length === 0) return [];
                    return data.map((d: any) => ({
                        color: d?.color,
                        label: d?.data?.series,
                        value: prettyBytes(parseInt(d?.data?.yValue, 10)),
                    }));
                };

                const tooltipData = {
                    title: getTitle(),
                    content: getContent(),
                };

                return (
                    <ChartCustomTooltip
                        title={tooltipData.title}
                        content={tooltipData.content}
                    />
                );
            },
        },
    };

    return (
        <AppChart
            type="line"
            data={chartData}
            config={chartConfig}
            summary={summary}
            loading={loading}
            displaySummary={displaySummary}
            testId={testId}
        />
    );
}

export default HaProxyBackendTrafficChart;
